import markerSvgBluey from '../assets/Pin_MitDaten.svg';
import markerSvgRed from '../assets/Pin_MitDaten-red.svg';
import maskerSvgLadestation from '../assets/Pin_Ladestationen.svg';
import mapDrawTooltipHelper from './MapDrawTooltipHelper';

export const REGIONS = {
  STATES: 'states',
  DISTRICS: 'districts'
};


class MapDrawService {
  constructor(mapApi, map, selectedYear, category) {
    this.mymap = map;
    this.tooltipOptions = { direction: 'top', opacity: 0.9 };
    this.mapApi = mapApi;
    this.category = category;
    this.selectedYear = selectedYear;
    this.renderedPolygonsWithGeoJson = [];
    this.renderedMarkers = [];
    this.renderedMarkerClusterGroup = {};
    this.colorMap = {
      1: '#4D0A42',
      2: 'url(#diagonal4D0A42)',
      3: '#CA6FBA',
      4: 'url(#diagonalCA6FBA)',
      5: '#E4B7DC',
      6: 'url(#diagonalE4B7DC))'
    };

    if (typeof window !== 'undefined') {
      this.L = require('leaflet');
      require('leaflet.markercluster/dist/leaflet.markercluster');
    }
  }

  renderGeoJsonsPolygons(region, geoJsonsWithRang) {
    geoJsonsWithRang.features.forEach(geoJson => {
      if (geoJson.geometry.type === 'Polygon' || geoJson.geometry.type === 'MultiPolygon') {
        this._renderPolygon(region, geoJson);
      }
    });
  }

  renderGeoJsonsMarkers(region, markerIcon, geoJsonsWithRang) {
    this.renderedMarkerClusterGroup = this._createMarkerGroup();

    geoJsonsWithRang.features.forEach(geoJson => {
      if (geoJson.geometry.type === 'Point') {
        const marker = markerIcon === 'ladestation'
          ? this._createLadestationMarker(geoJson)
          : this._createCityMarker(region, geoJson);

        this.renderedMarkers.push({ marker, geoJson });
        this.renderedMarkerClusterGroup.addLayer(marker);
      }
    });

    this.mymap.addLayer(this.renderedMarkerClusterGroup);
  }

  clearMap() {
    this.renderedPolygonsWithGeoJson.forEach(({ polygon }) => polygon.remove());
    this.renderedPolygonsWithGeoJson = [];
    this.renderedMarkers = [];
    this.mymap.removeLayer(this.renderedMarkerClusterGroup);
    this.renderedMarkerClusterGroup = {};
  }

  resetAllTooltipsForPolygon(region) {
    this.renderedPolygonsWithGeoJson.forEach(({ polygon, geoJson }) => {
      polygon.closeTooltip();
      polygon.unbindTooltip();
      polygon.off('mouseover');
      polygon.off('mouseout');

      this._addTooltipForPolygon(region, geoJson, polygon);
      this._addHoverForPolygon(geoJson, polygon);
    });
  }

  updateRankOfPolygons(ranks) {
    if (!ranks) {
      this.renderedPolygonsWithGeoJson.forEach(({ polygon }) => {
        polygon.setStyle({ fillColor: '#fff', color: '#eee' });
      });

      return;
    }

    this.renderedPolygonsWithGeoJson.forEach(({ polygon, geoJson }) => {
      const foundRank = ranks.find(rank => {
        return rank.ags === geoJson.properties.ags;
      });

      if (foundRank) {
        polygon.setStyle({
          fillColor: this.colorMap[foundRank.rank] || '#fff',
          color: '#fff'
        });
      } else {
        polygon.setStyle({ fillColor: '#fff', color: '#eee' });
      }
    });
  }

  setCategory(category) {
    this.category = category;
  }

  setSelectedYear(selectedYear) {
    this.selectedYear = selectedYear;
  }

  setTooltipKey(tooltipKey) {
    this.tooltipKey = tooltipKey;
  }

  setMapApi(mapApi) {
    this.mapApi = mapApi;
  }

  _renderPolygon(region, geoJson) {
    const latlngs = geoJson.geometry.coordinates;
    const polygon = this.L.polygon(latlngs, {
      color: '#F8F5F2',
      weight: 2,
      fillColor: this.colorMap[geoJson.properties.rank] || '#fff',
      opacity: 0.7,
      fillOpacity: 1.0
    }).addTo(this.mymap);

    this.renderedPolygonsWithGeoJson.push({ polygon, geoJson });

    this._addTooltipForPolygon(region, geoJson, polygon);
    this._addHoverForPolygon(geoJson, polygon);
  }

  _addHoverForPolygon(geoJson, polygon) {
    const elementID = geoJson.properties.id;

    polygon.on('mouseover', () => {
      this.renderedPolygonsWithGeoJson.forEach(({ polygon, geoJson }) => {
        if (geoJson.properties.id !== elementID) {
          polygon.setStyle({ fillOpacity: 0.5 });
        }
      });
    });

    polygon.on('mouseout', () => {
      this.renderedPolygonsWithGeoJson.forEach(({ polygon }) => {
        polygon.setStyle({ fillOpacity: 1 });
      });
    });
  }

  _addTooltipForPolygon(region, geoJson, polygon) {
    polygon.bindTooltip(
      `<h3>${geoJson.properties.id}</h3>
      <div class="tooltip-loader">Loading...</div>`,
      this.tooltipOptions
    );

    let mouseOnPolygon = false;

    polygon.on('mouseover', async () => {
      mouseOnPolygon = true;
      if (!polygon.getTooltip()._content.includes('loader')) {
        return;
      }

      const tooltipContent = await this.mapApi.fetchTooltip(
        region,
        geoJson.properties.ags,
        this.selectedYear,
        this.category
      );

      const tooltipTextHTML = mapDrawTooltipHelper.createTooltipHtml(
        this.tooltipKey,
        geoJson,
        tooltipContent,
        this.selectedYear,
        region
      );

      polygon.unbindTooltip();
      polygon.bindTooltip(tooltipTextHTML, this.tooltipOptions);

      if (mouseOnPolygon) {
        polygon.openTooltip();
      }
    });

    polygon.on('mouseout', () => {
      mouseOnPolygon = false;
    });
  }

  _createCityMarker(region, geoJson) {
    const BasicMarkerIcon = this._createBasicMarkerIcon();
    const markerIconBluey = new BasicMarkerIcon({ iconUrl: markerSvgBluey });
    const markerIconRed = new BasicMarkerIcon({ iconUrl: markerSvgRed });
    const marker = this.L.marker({
      lon: geoJson.geometry.coordinates[0],
      lat: geoJson.geometry.coordinates[1]
    }, {
      icon: markerIconBluey
    });

    this._addTooltipForMarker(region, geoJson, marker);
    this._addHoverForMarker(marker, markerIconBluey, markerIconRed);

    return marker;
  }

  _addTooltipForMarker(region, geoJson, marker) {
    marker.bindTooltip(
      `<h3>${geoJson.properties.id}</h3>
    <div class="tooltip-loader">Loading...</div>`,
      this.tooltipOptions
    );

    let mouseOnMarker = false;

    marker.on('mouseover', async () => {
      mouseOnMarker = true;

      if (!marker.getTooltip()._content.includes('loader')) {
        return;
      }

      const tooltipContent = await this.mapApi.fetchTooltip(
        region,
        geoJson.properties.ags,
        this.selectedYear,
        this.category
      );

      let tooltipTextHTML;
      if (tooltipContent) {
        // Use MapDrawTooltipHelper, when cities are back :)
        // See tooltip stuff for polygon
        tooltipTextHTML = `<h3>${geoJson.properties.id}</h3>
          <ul>
          <li>Patente: ${tooltipContent.value[0].amount}</li>
          </ul>`;
      } else {
        tooltipTextHTML = `<h3>${geoJson.properties.id}</h3>
        <ul>
        <li>Bisher keine Regional-Daten verfügbar oder Wert = 0,00</li>
        </ul>`;
      }

      marker.unbindTooltip();
      marker.bindTooltip(tooltipTextHTML, this.tooltipOptions);

      if (mouseOnMarker) {
        marker.openTooltip();
      }
    });

    marker.on('mouseout', () => {
      mouseOnMarker = false;
    });
  }

  _addHoverForMarker(marker, markerIconBluey, markerIconRed) {
    marker.on('mouseover', () => marker.setIcon(markerIconRed));
    marker.on('mouseout', () => marker.setIcon(markerIconBluey));
  }

  _createLadestationMarker(geoJson) {
    const BasicMarkerIcon = this._createBasicMarkerIcon();
    const maskerIconLadestation = new BasicMarkerIcon({
      iconUrl: maskerSvgLadestation
    });

    return this.L.marker({
      lon: geoJson.geometry.coordinates[0],
      lat: geoJson.geometry.coordinates[1]
    }, {
      icon: maskerIconLadestation
    });
  }

  _createBasicMarkerIcon() {
    return this.L.Icon.extend({
      options: {
        iconSize: [38, 38],
        iconAnchor: [19, 38],
        tooltipAnchor: [0, -35]
      }
    });
  }

  _createMarkerGroup() {
    const markers = this.L.markerClusterGroup({
      showCoverageOnHover: false,
      zoomToBoundsOnClick: false,
      spiderfyOnMaxZoom: false,
      // 13 for example is not performant -> too many markers
      disableClusteringAtZoom: 14,
      maxClusterRadius: mapZoom => {
        if (mapZoom <= 8) {
          return 80;
        }

        return 175;
      }
    });

    return markers;
  }
}

export default MapDrawService;